import React, { useMemo } from "react";
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { InstrumentDiscount } from "../clients/answeringClient";

type DisplayDiscount = InstrumentDiscount & {
  isNoDiscount?: boolean;
};

const buildDisplayDiscounts = (
  discounts: InstrumentDiscount[]
): DisplayDiscount[] => {
  if (discounts.length === 0) {
    return [];
  }

  const minQtyWithDiscount = Math.min(
    ...discounts.map((discount) => discount.minQty)
  );

  const shouldAddNoDiscount = minQtyWithDiscount > 1;

  const sortedDiscounts = [...discounts].sort((first, second) => {
    if (second.discountPct !== first.discountPct) {
      return second.discountPct - first.discountPct;
    }
    return first.minQty - second.minQty;
  });

  if (!shouldAddNoDiscount) {
    return sortedDiscounts;
  }

  return [
    ...sortedDiscounts,
    {
      minQty: 1,
      maxQty: minQtyWithDiscount - 1,
      discountPct: 0,
      unitPriceOverrideCents: null,
      discountedUnitPriceCents: null,
      isNoDiscount: true,
    },
  ];
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-br", {
    style: "currency",
    currency: "BRL",
  }).format(value / 100);

interface DiscountTiersListProps {
  discounts: InstrumentDiscount[];
  baseUnitPriceCents: number | null;
  appliedQuantity?: number | null;
  onSelectTier?: (minQuantity: number) => void;
}

const DiscountTiersList: React.FC<DiscountTiersListProps> = ({
  discounts,
  baseUnitPriceCents,
  appliedQuantity,
  onSelectTier,
}) => {
  const hasUnitPriceOverride = useMemo(
    () => discounts.some((discount) => discount.unitPriceOverrideCents !== null),
    [discounts]
  );

  const displayDiscounts = useMemo(
    () => buildDisplayDiscounts(discounts),
    [discounts]
  );

  const discountRange = useMemo(() => {
    const validDiscounts = displayDiscounts.filter(
      (discount) => !discount.isNoDiscount
    );

    if (validDiscounts.length === 0) {
      return null;
    }

    return {
      min: Math.min(...validDiscounts.map((discount) => discount.discountPct)),
      max: Math.max(...validDiscounts.map((discount) => discount.discountPct)),
    };
  }, [displayDiscounts]);

  const getDiscountColors = (discount: DisplayDiscount) => {
    if (discount.isNoDiscount || !discountRange) {
      return {
        accentText: "grey.800",
        priceColor: "primary.dark",
        helperText: "grey.600",
      };
    }

    const baseColor = '#F19421';

    return {
      accentText: baseColor,
      priceColor: baseColor,
      helperText: alpha(baseColor, 0.85),
    };
  };

  const isTierActive = (discount: DisplayDiscount) => {
    if (!appliedQuantity || appliedQuantity <= 0) {
      return false;
    }

    const meetsMin = appliedQuantity >= discount.minQty;
    const withinMax =
      discount.maxQty == null ? true : appliedQuantity <= discount.maxQty;

    return meetsMin && withinMax;
  };

  if (displayDiscounts.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={1.25}>
      {displayDiscounts.map((discount) => {
        const discountedPriceCents = discount.isNoDiscount
          ? baseUnitPriceCents
          : discount.discountedUnitPriceCents ??
          discount.unitPriceOverrideCents ??
          null;

        const shouldShowOriginalPrice =
          !discount.isNoDiscount &&
          baseUnitPriceCents !== null &&
          discountedPriceCents !== null &&
          discountedPriceCents !== baseUnitPriceCents;

        const colors = getDiscountColors(discount);
        const isActiveTier = isTierActive(discount);
        const handleChipClick = () => {
          if (onSelectTier) {
            onSelectTier(discount.minQty);
          }
        };
        const isSameAsApplied = appliedQuantity === discount.minQty;
        const canShowStatusChip = Boolean(onSelectTier) || isActiveTier;

        return (
          <Grid
            item
            xs={12}
            sm={6}
            key={`${discount.minQty}-${discount.maxQty ?? "max"}-${discount.isNoDiscount ? "no" : "yes"
              }`}
            >
              <Card
                elevation={0}
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "grey.200",
                  display: "flex",
                  backgroundColor: isActiveTier ? discount.isNoDiscount ? "grey.300" : alpha(colors.accentText, 0.08) : "grey.200",
                  height: '-webkit-fill-available',
                }}
              >
                {canShowStatusChip && (
                  <Chip
                    size="small"
                    color={isActiveTier ? "primary" : "secondary"}
                    label={
                      isActiveTier
                        ? "Em uso"
                        : `Aplicar`
                    }
                    icon={isActiveTier ? <TaskAltIcon/>:<RadioButtonUncheckedIcon />}
                    variant={isActiveTier ? "filled" : "outlined"}
                    clickable={!isActiveTier && Boolean(onSelectTier)}
                    onClick={
                      !isActiveTier && onSelectTier && !isSameAsApplied
                        ? handleChipClick
                        : undefined
                    }
                    disabled={!isActiveTier && isSameAsApplied}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      fontWeight: 600,
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: { xs: "40%", md: "32%" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.accentText,
                    px: { xs: 1.5, md: 2.5 },
                  }}
                >
                <Stack spacing={0} textAlign={"center"}>
                  <Typography
                    variant={discount.isNoDiscount ? "h5" : "h4"}
                    fontWeight={900}
                    sx={{
                      color: 'white',
                    }}
                  >
                    {discount.isNoDiscount
                      ? "Sem desconto"
                      : `${discount.discountPct}% OFF`}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: "white" }}
                  >
                    {discount.maxQty != null
                      ? `${discount.minQty} - ${discount.maxQty}`
                      : `A partir de ${discount.minQty}`}
                  </Typography>
                </Stack>
              </Box>

              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                  px: { xs: 2, md: 3 },
                  py: { xs: 4, md: 4 },
                }}
              >
                <Stack spacing={0.5} >

                  {shouldShowOriginalPrice && (
                    <Box display={"flex"} gap={1.5} alignItems={"center"}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "secondary.main",
                          textDecoration: "line-through",
                        }}
                      >
                        {formatCurrency(baseUnitPriceCents!)} /und
                      </Typography>
                    </Box>
                  )}

                  {discountedPriceCents !== null && (
                    <Box
                      display={"flex"}
                      alignContent={"center"}
                      alignItems={"end"}
                      flexWrap={'wrap'}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={800}
                        sx={{ color: colors.priceColor, mr:0.5 }}
                      >
                        {formatCurrency(discountedPriceCents)}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                      >
                        /und
                      </Typography>
                    </Box>
                  )}

                </Stack>

                {hasUnitPriceOverride && !discount.isNoDiscount && (
                  <Typography variant="body2" sx={{ color: "grey.700" }}>
                    Preço unitário aplicado:{" "}
                    <Box
                      component="span"
                      fontWeight={700}
                      sx={{ color: "primary.dark" }}
                    >
                      {discount.unitPriceOverrideCents !== null
                        ? formatCurrency(discount.unitPriceOverrideCents)
                        : "-"}
                    </Box>
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default DiscountTiersList;
